import React from 'react'
import { ID, UUID } from './self-tour.service.types'
import { Nilable } from 'tsdef'

export const localStorageKeys = {
  userAgreement: 'SELF_TOUR_AGREEMENT',
  pricingTransparency: 'SELF_TOUR_PRICING_TRANSPARENCY',
}

export function selfTourAgreementKey(uuid: ID) {
  return `${localStorageKeys.userAgreement}-${uuid}`
}

export const LocalStorageContext = React.createContext<Storage>(
  window.localStorage
)
export const LocalStorageProvider = LocalStorageContext.Provider

export function useLocalStorage() {
  return React.useContext(LocalStorageContext)
}

/**
 * Store the user's acknowledgment of the self tour agreement
 * in the user's device local storage.
 */
export function useSignSelfTourUserAgreement(guestCardUuid: UUID) {
  const localStore = useLocalStorage()
  const key = selfTourAgreementKey(guestCardUuid)

  const [value, setValue] = React.useState(localStore.getItem(key))

  return [
    value === 'true',
    React.useCallback(() => {
      if (guestCardUuid) {
        console.log('setting', key, 'to true')
        // Save the agreement to local storage.
        localStore.setItem(key, 'true')
        setValue('true')
      } else {
        console.warn(
          'Cannot save Self Tour User Agreement because Guest Card UUID does not exist.'
        )
      }
    }, [localStore, key, guestCardUuid]),
  ] as const
}

export function pricingTransparencyKey(uuid: ID) {
  return `${localStorageKeys.pricingTransparency}-${uuid}`
}

/**
 * A hook to get and set whether the user has seen the pricing transparency modal.
 */
export function useHasSeenPricingTransparencyModal(
  communityId: Nilable<ID>,
  disableStorage?: boolean
) {
  const localStore = useLocalStorage()

  const [hasSeen, setHasSeen] = React.useState(
    // If storage is disabled, always return false (not seen). This allows our
    // storybook to always show the modal.
    communityId &&
      !disableStorage &&
      localStore.getItem(pricingTransparencyKey(communityId)) === 'true'
  )

  const markAsSeen = React.useCallback(() => {
    if (!disableStorage && communityId) {
      localStore.setItem(pricingTransparencyKey(communityId), 'true')
    }
    setHasSeen(true)
  }, [localStore, communityId, disableStorage])

  return [hasSeen, markAsSeen] as const
}
